import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ScenarioModeling = () => {
  const [scenarios, setScenarios] = useState([]);
  const [activeScenario, setActiveScenario] = useState('base');
  const [modelingParams, setModelingParams] = useState({
    carbonTax: 50,
    oilPrice: 80,
    renewableShare: 35,
    economicGrowth: 2.5,
    weatherImpact: 0
  });
  const [results, setResults] = useState([]);
  const [isCalculating, setIsCalculating] = useState(false);

  const scenarioOptions = [
    { value: 'base', label: 'Base Case', description: 'Current market conditions' },
    { value: 'bullish', label: 'Bullish Scenario', description: 'Optimistic market outlook' },
    { value: 'bearish', label: 'Bearish Scenario', description: 'Pessimistic market outlook' },
    { value: 'stress', label: 'Stress Test', description: 'Extreme market conditions' },
    { value: 'custom', label: 'Custom Scenario', description: 'User-defined parameters' }
  ];

  const predefinedScenarios = {
    base: { carbonTax: 50, oilPrice: 80, renewableShare: 35, economicGrowth: 2.5, weatherImpact: 0 },
    bullish: { carbonTax: 75, oilPrice: 95, renewableShare: 45, economicGrowth: 4.0, weatherImpact: -10 },
    bearish: { carbonTax: 30, oilPrice: 60, renewableShare: 25, economicGrowth: 0.5, weatherImpact: 15 },
    stress: { carbonTax: 100, oilPrice: 120, renewableShare: 60, economicGrowth: -2.0, weatherImpact: 25 },
    custom: modelingParams
  };

  useEffect(() => {
    if (activeScenario !== 'custom') {
      setModelingParams(predefinedScenarios?.[activeScenario]);
    }
  }, [activeScenario]);

  useEffect(() => {
    calculateScenario();
  }, [modelingParams]);

  const calculateScenario = () => {
    setIsCalculating(true);
    
    setTimeout(() => {
      const basePrice = 85;
      const data = [];
      
      for (let i = 0; i < 12; i++) {
        const month = new Date(2024, i, 1)?.toLocaleDateString('en-US', { month: 'short' });
        
        // Complex price calculation based on parameters
        const carbonTaxImpact = (modelingParams?.carbonTax - 50) * 0.3;
        const oilPriceImpact = (modelingParams?.oilPrice - 80) * 0.2;
        const renewableImpact = (modelingParams?.renewableShare - 35) * -0.15;
        const growthImpact = (modelingParams?.economicGrowth - 2.5) * 2;
        const weatherImpact = modelingParams?.weatherImpact * 0.1;
        
        const totalImpact = carbonTaxImpact + oilPriceImpact + renewableImpact + growthImpact + weatherImpact;
        const seasonalVariation = Math.sin((i / 12) * 2 * Math.PI) * 5;
        const randomVariation = (Math.random() - 0.5) * 8;
        
        const euaPrice = basePrice + totalImpact + seasonalVariation + randomVariation;
        const oilPrice = modelingParams?.oilPrice + (Math.random() - 0.5) * 10;
        
        data?.push({
          month,
          euaPrice: parseFloat(euaPrice?.toFixed(2)),
          oilPrice: parseFloat(oilPrice?.toFixed(2)),
          confidence: Math.max(60, 95 - Math.abs(totalImpact) * 2),
          volatility: Math.max(5, 15 + Math.abs(totalImpact) * 0.5)
        });
      }
      
      setResults(data);
      setIsCalculating(false);
    }, 1000);
  };

  const handleParameterChange = (param, value) => {
    const newParams = { ...modelingParams, [param]: parseFloat(value) };
    setModelingParams(newParams);
    if (activeScenario !== 'custom') {
      setActiveScenario('custom');
    }
  };

  const saveScenario = () => {
    const scenarioName = prompt('Enter scenario name:');
    if (scenarioName) {
      const newScenario = {
        id: Date.now(),
        name: scenarioName,
        params: { ...modelingParams },
        results: [...results],
        createdAt: new Date()?.toISOString()
      };
      setScenarios([...scenarios, newScenario]);
    }
  };

  const exportResults = () => {
    const csvContent = "data:text/csv;charset=utf-8," + "Month,EUA Price,Oil Price,Confidence,Volatility\n" +
      results?.map(row => `${row?.month},${row?.euaPrice},${row?.oilPrice},${row?.confidence},${row?.volatility}`)?.join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link?.setAttribute("href", encodedUri);
    link?.setAttribute("download", `scenario_${activeScenario}_${new Date()?.toISOString()?.split('T')?.[0]}.csv`);
    document.body?.appendChild(link);
    link?.click();
    document.body?.removeChild(link);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Scenario Modeling</h3>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={saveScenario}
            iconName="Save"
            iconSize={14}
          >
            Save Scenario
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportResults}
            iconName="Download"
            iconSize={14}
          >
            Export
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Parameters Panel */}
        <div className="space-y-6">
          <div>
            <Select
              label="Scenario Template"
              options={scenarioOptions}
              value={activeScenario}
              onChange={setActiveScenario}
              description="Choose a predefined scenario or create custom"
            />
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-foreground">Model Parameters</h4>
            
            <Input
              label="Carbon Tax (€/tonne)"
              type="number"
              value={modelingParams?.carbonTax}
              onChange={(e) => handleParameterChange('carbonTax', e?.target?.value)}
              description="Current: €50/tonne"
            />
            
            <Input
              label="Oil Price ($/barrel)"
              type="number"
              value={modelingParams?.oilPrice}
              onChange={(e) => handleParameterChange('oilPrice', e?.target?.value)}
              description="Current: $80/barrel"
            />
            
            <Input
              label="Renewable Share (%)"
              type="number"
              value={modelingParams?.renewableShare}
              onChange={(e) => handleParameterChange('renewableShare', e?.target?.value)}
              description="Current: 35%"
            />
            
            <Input
              label="Economic Growth (%)"
              type="number"
              step="0.1"
              value={modelingParams?.economicGrowth}
              onChange={(e) => handleParameterChange('economicGrowth', e?.target?.value)}
              description="Annual GDP growth"
            />
            
            <Input
              label="Weather Impact"
              type="number"
              value={modelingParams?.weatherImpact}
              onChange={(e) => handleParameterChange('weatherImpact', e?.target?.value)}
              description="Temperature deviation (°C)"
            />
          </div>

          {/* Saved Scenarios */}
          {scenarios?.length > 0 && (
            <div>
              <h4 className="font-medium text-foreground mb-3">Saved Scenarios</h4>
              <div className="space-y-2">
                {scenarios?.slice(-3)?.map(scenario => (
                  <div key={scenario?.id} className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">{scenario?.name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setModelingParams(scenario?.params);
                          setActiveScenario('custom');
                        }}
                        iconName="Play"
                        iconSize={12}
                      >
                        Load
                      </Button>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {new Date(scenario.createdAt)?.toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Results Visualization */}
        <div className="lg:col-span-2">
          <div className="mb-4">
            <h4 className="font-medium text-foreground mb-2">Scenario Results</h4>
            {isCalculating && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="animate-spin">
                  <Icon name="Loader2" size={16} />
                </div>
                <span>Calculating scenario...</span>
              </div>
            )}
          </div>

          <div className="h-80 mb-6">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={results} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={12}
                  tickFormatter={(value) => `€${value}`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-popover-foreground)'
                  }}
                  formatter={(value, name) => {
                    if (name === 'euaPrice') return [`€${value}`, 'EUA Price'];
                    if (name === 'oilPrice') return [`$${value}`, 'Oil Price'];
                    if (name === 'confidence') return [`${value}%`, 'Confidence'];
                    return [value, name];
                  }}
                />
                <Legend />
                
                <Line 
                  type="monotone" 
                  dataKey="euaPrice" 
                  stroke="var(--color-primary)" 
                  strokeWidth={2}
                  name="EUA Price"
                />
                <Line 
                  type="monotone" 
                  dataKey="oilPrice" 
                  stroke="var(--color-warning)" 
                  strokeWidth={2}
                  name="Oil Price"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Confidence Interval */}
          <div className="h-32">
            <h5 className="text-sm font-medium text-foreground mb-2">Confidence Interval</h5>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={results}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                <XAxis 
                  dataKey="month" 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                />
                <YAxis 
                  stroke="var(--color-muted-foreground)"
                  fontSize={10}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip 
                  contentStyle={{
                    backgroundColor: 'var(--color-popover)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    color: 'var(--color-popover-foreground)'
                  }}
                  formatter={(value) => [`${value}%`, 'Confidence']}
                />
                <Area 
                  type="monotone" 
                  dataKey="confidence" 
                  stroke="var(--color-accent)" 
                  fill="var(--color-accent)"
                  fillOpacity={0.3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Summary Statistics */}
          {results?.length > 0 && (
            <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-sm text-muted-foreground">Avg EUA Price</div>
                <div className="text-lg font-semibold text-foreground">
                  €{(results?.reduce((sum, r) => sum + r?.euaPrice, 0) / results?.length)?.toFixed(2)}
                </div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-sm text-muted-foreground">Price Range</div>
                <div className="text-lg font-semibold text-foreground">
                  €{Math.min(...results?.map(r => r?.euaPrice))?.toFixed(0)} - €{Math.max(...results?.map(r => r?.euaPrice))?.toFixed(0)}
                </div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-sm text-muted-foreground">Avg Confidence</div>
                <div className="text-lg font-semibold text-foreground">
                  {(results?.reduce((sum, r) => sum + r?.confidence, 0) / results?.length)?.toFixed(1)}%
                </div>
              </div>
              <div className="p-3 bg-muted rounded-lg">
                <div className="text-sm text-muted-foreground">Volatility</div>
                <div className="text-lg font-semibold text-foreground">
                  {(results?.reduce((sum, r) => sum + r?.volatility, 0) / results?.length)?.toFixed(1)}%
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScenarioModeling;